import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import {
  ADE_ONLINE_UPDATES_FEATURE_FLAG,
  EVENTS_HEATMAP_PAGE_FF,
  DIAGNOSTIC_PAGE,
  AMF_DATA_EXPLORER_PAGE_FF,
  ASTRONAUT_MAX_VISIBILITY_FF,
  N_CAPTURE_VISIBILITY_FF,
  PAGE_NOT_FOUND_MESSAGE,
  FORBIDDEN_PAGE_MESSAGE,
  ACCESS_DENIED_MESSAGE,
  VECTOR_SETTINGS_VISIBILITY_FF,
} from '../../common/constants';
import { featureFlagsSelectors } from '../../reducers/featureFlagSlice';
import { useAppSelector } from '../../reducers/rootReducer';
import RoutesProtector from './RoutesProtector';
import LogoutPage from '../../containers/LogoutPage/LogoutPage';
import DataChartConfigGate from '../../common/components/DataChartConfigGate';
import Preloader from '../Preloader/Preloader';
import PreferredSystemsGuard from './PreferredSystemsGuard';
import { useLelyAuth } from '@/auth/useLelyAuth';
import ThemePlaygroundPage from '@/components/ThemePlayground/ThemePlaygroundPage';
import DiagnosticsPage from '@/containers/DiagnosticsPage/DiagnosticsPage';

const GeneralError = React.lazy(
  () => import('../../components/GeneralError/GeneralError'),
);
const AstronautDataExplorerPageComponent = React.lazy(
  () =>
    import(
      '../../containers/AstronautDataExplorerPage/AstronautDataExplorerPage'
    ),
);
const VectorPageWrapperComponent = React.lazy(
  () => import('../../containers/VectorPage/VectorPageWrapper'),
);
const VectorSettingsComponent = React.lazy(
  () => import('../../containers/VectorSettingsPage/VectorSettingsPage'),
);
const EventsPageComponent = React.lazy(
  () => import('../../containers/EventsPage/EventsPage'),
);
const EventsPageVDEComponent = React.lazy(
  () => import('../../containers/EventsPageVDE/EventsPageVDE'),
);
const BackupSettingsComponent = React.lazy(
  () => import('../../containers/BackupSettingsPage/BackupSettingsPage'),
);
const SettingsComponent = React.lazy(
  () => import('../../containers/SettingsPage/SettingsPage'),
);
const FarmsPageComponent = React.lazy(
  () => import('../../containers/FarmsPage/FarmsPage'),
);
const FarmDetailsComponent = React.lazy(
  () => import('../../containers/FarmDetailsPage/FarmDetailsPage'),
);
const EntityDetailsComponent = React.lazy(
  () => import('../../containers/EntityDetailsPage/EntityDetailsPage'),
);
const LogsPageComponent = React.lazy(
  () => import('../../containers/LogsPage/LogsPage'),
);
const AMFSettingsComponent = React.lazy(
  () => import('../../containers/AMFSettingsPage/AMFSettingsPage'),
);
const DiscoveryPageComponent = React.lazy(
  () => import('../../containers/DiscoveryPage/DiscoveryPage'),
);
const HeatmapPageComponent = React.lazy(
  () => import('../../containers/HeatmapPage/HeatmapPage'),
);
const UpdatesDetailsComponent = React.lazy(
  () => import('../../containers/UpdatesDetailsPage/UpdatesDetailsPage'),
);
const HubDevicesComponent = React.lazy(
  () => import('../../containers/HubDevicesPage/HubDevicesPage'),
);

const PerformanceIndicatorsPageComponent = React.lazy(
  () =>
    import(
      '../../containers/PerformanceIndicatorsPage/PerformanceIndicatorsPage'
    ),
);

const AstronautMaxDataExplorerPageComponent = React.lazy(
  () =>
    import(
      '../../containers/AstronautMaxDataExplorerPage/AstronautMaxDataExplorerPage'
    ),
);

const JunoPageComponent = React.lazy(
  () => import('../../containers/JunoPage/JunoPage'),
);

const HomePageComponent = React.lazy(() => import('../Home/Home'));

function TopLevelRouter() {
  const { user, isAuthenticated } = useLelyAuth();
  const isHubDevicesPageActive = user?.allowhub ?? false;
  const isStrypesUser = user?.usertype === 'Strypes';
  const location = useLocation();
  const pathName = location.pathname;
  const loadedFF = useAppSelector((state) => state.featureFlag.loaded);

  const isActiveUpdates = useAppSelector((state) =>
    featureFlagsSelectors.isFlagActive(state, ADE_ONLINE_UPDATES_FEATURE_FLAG),
  );
  const isDiagnosticPageActive = useAppSelector((state) =>
    featureFlagsSelectors.isFlagActive(state, DIAGNOSTIC_PAGE),
  );
  const isAmfDataExplorerPageActive = useAppSelector((state) =>
    featureFlagsSelectors.isFlagActive(state, AMF_DATA_EXPLORER_PAGE_FF),
  );
  const isHeatmapPageActive = useAppSelector((state) =>
    featureFlagsSelectors.isFlagActive(state, EVENTS_HEATMAP_PAGE_FF),
  );
  const isAstronautMaxPageActive = useAppSelector((state) =>
    featureFlagsSelectors.isFlagActive(state, ASTRONAUT_MAX_VISIBILITY_FF),
  );
  const isNCapturePageActive = useAppSelector((state) =>
    featureFlagsSelectors.isFlagActive(state, N_CAPTURE_VISIBILITY_FF),
  );
  const isVectorSettingsPageActive = useAppSelector((state) =>
    featureFlagsSelectors.isFlagActive(state, VECTOR_SETTINGS_VISIBILITY_FF),
  );

  const isUnauthorized = isAuthenticated && user != null && !user.allowltda;
  if (!loadedFF && pathName !== '/logout' && !isUnauthorized) {
    return null;
  }

  return (
    <Routes>
      <Route path="/" element={<HomePageComponent />} />
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route element={<RoutesProtector />}>
        <Route path="/farms">
          <Route index element={<FarmsPageComponent />} />
          <Route path=":farmId" element={<FarmDetailsComponent />} />
        </Route>
        <Route path="/settings">
          <Route index element={<FarmsPageComponent />} />
        </Route>
        <Route path="/feedback">
          <Route index element={<FarmsPageComponent />} />
        </Route>

        {isHubDevicesPageActive && (
          <Route path="/hub-devices" element={<HubDevicesComponent />} />
        )}

        <Route
          path="/entities/:externalId/astronaut"
          element={<PreferredSystemsGuard entityType="AstronautA5Next" />}
        >
          <Route path="overview" element={<EntityDetailsComponent />} />
          <Route path="events" element={<EventsPageComponent />} />
          <Route path="logs" element={<LogsPageComponent />} />
          <Route path="settings" element={<SettingsComponent />} />
          <Route
            path="settings/backups"
            element={<BackupSettingsComponent />}
          />

          <Route
            path="data-explorer"
            element={
              <DataChartConfigGate>
                <AstronautDataExplorerPageComponent />
              </DataChartConfigGate>
            }
          />

          {isDiagnosticPageActive && (
            <Route path="domains/canbus" element={<DiagnosticsPage />} />
          )}
          {isActiveUpdates && (
            <Route path="updates" element={<UpdatesDetailsComponent />} />
          )}
        </Route>

        <Route
          path="/entities/:externalId/vector"
          element={<PreferredSystemsGuard entityType="VectorSystem" />}
        >
          <Route path="events" element={<EventsPageVDEComponent />} />
          <Route
            path="performance-indicators"
            element={<PerformanceIndicatorsPageComponent />}
          />
          <Route
            path="data-explorer"
            element={<VectorPageWrapperComponent />}
          />

          {isVectorSettingsPageActive && (
            <Route path="settings" element={<VectorSettingsComponent />} />
          )}
        </Route>

        <Route
          path="/entities/:externalId/amf"
          element={<PreferredSystemsGuard entityType="AutomaticMilkFilter" />}
        >
          <Route path="settings" element={<AMFSettingsComponent />} />
          <Route path="events" element={<EventsPageComponent />} />
          {isAmfDataExplorerPageActive && (
            <Route
              path="data-explorer"
              element={
                <DataChartConfigGate>
                  <AstronautDataExplorerPageComponent />
                </DataChartConfigGate>
              }
            />
          )}
        </Route>

        {isNCapturePageActive && (
          <Route
            path="/entities/:externalId/n-capture"
            element={<PreferredSystemsGuard entityType="NCapture" />}
          >
            <Route
              path="data-explorer"
              element={
                <DataChartConfigGate>
                  <AstronautDataExplorerPageComponent />
                </DataChartConfigGate>
              }
            />
          </Route>
        )}

        {isAstronautMaxPageActive && (
          <Route
            path="/entities/:externalId/astronaut-max"
            element={<PreferredSystemsGuard entityType="AstronautMax" />}
          >
            <Route
              path="data-explorer"
              element={<AstronautMaxDataExplorerPageComponent />}
            />
          </Route>
        )}

        <Route
          path="/entities/:externalId/juno"
          element={<PreferredSystemsGuard entityType="Juno" />}
        >
          <Route path="logs" element={<JunoPageComponent />} />
        </Route>

        <Route
          path="/entities/:externalId/discovery"
          element={<PreferredSystemsGuard entityType="Discovery" />}
        >
          <Route path="logs" element={<DiscoveryPageComponent />} />
          {isHeatmapPageActive && (
            <Route path="events-heatmap" element={<HeatmapPageComponent />} />
          )}
        </Route>
      </Route>
      <Route
        path="/access-denied"
        element={
          <GeneralError
            title="Access denied"
            message={ACCESS_DENIED_MESSAGE}
            icon="lely-icon-warning-large"
          />
        }
      />
      <Route
        path="/forbidden"
        element={
          <GeneralError
            message={FORBIDDEN_PAGE_MESSAGE}
            title="Forbidden"
            icon="lely-icon-warning-large"
          />
        }
      />
      <Route
        path="/not-found"
        element={
          <GeneralError
            title="Page not found"
            icon="lely-icon-warning-large"
            message={PAGE_NOT_FOUND_MESSAGE}
          />
        }
      />
      {isStrypesUser && (
        <Route path="/playground" element={<ThemePlaygroundPage />} />
      )}
      <Route path="/auth/callback" element={<Preloader />} />
      <Route path="/logout" element={<LogoutPage />} />
      <Route
        path="/404-error"
        element={
          <GeneralError
            title="Page not found"
            icon="lely-icon-not-found"
            message={PAGE_NOT_FOUND_MESSAGE}
          />
        }
      />
      <Route path="*" element={<Navigate to="/404-error" />} />
    </Routes>
  );
}

export default TopLevelRouter;
